import cn from 'classnames';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { SortStartHandler } from 'react-sortable-hoc';
import Icon from 'components/Icon';
import Tooltip from 'components/Tooltip';
import { TextArea } from 'components/form';
import { Col, Row } from 'components/layout';
import { SortableItem, SortableList, Trigger } from 'components/Sortable';

import { CompanyRequest } from 'lib/models/company';
import AddNewCard from '../AddNewCard';
import EditableCard from '../EditableCard';
import Gamification, { GamificationProps } from '../Gamification';
import SectionWrapper from '../SectionWrapper';

import styles from './IdentityDetails.module.scss';

export interface IdentityDetailsProps extends GamificationProps {
  showProgressBanner: boolean;
  sectionClass?: string;
}

export const IdentityDetails: React.FC<IdentityDetailsProps> = memo(
  ({
    showProgressBanner,
    progressDetails,
    profileCompletePercent,
    nonCompletedSection,
    handleProfileCompletionButton,
    handleNextButton,
    showNextButton,
    sectionClass,
  }: IdentityDetailsProps) => {
    const {
      register,
      formState: { errors },
      control,
      watch,
    } = useFormContext();

    const [dragItemIndex, setDragItemIndex] = useState<number | null>(null);
    const [hoverItemIndex, setHoverItemIndex] = useState<number | null>(null);
    const [showProgressBannerState, setShowProgressBannerState] =
      React.useState<boolean>(showProgressBanner);

    useEffect(() => {
      if (showProgressBannerState === false)
        setShowProgressBannerState(showProgressBanner);
    }, [showProgressBanner]);

    const { update, append, move } = useFieldArray({
      control,
      name: 'culture_stats_attributes',
    });

    const cultureStats: CompanyRequest['culture_stats_attributes'] = watch(
      'culture_stats_attributes'
    );

    const remove = (index: number) => {
      const updatedValues = cultureStats.filter((item) => !item._destroy)[
        index
      ];
      const indexToUpdate = cultureStats.indexOf(updatedValues);
      updatedValues._destroy = true;
      update(indexToUpdate, updatedValues);
    };

    const onSortStart: SortStartHandler = ({ index }) => {
      setDragItemIndex(index);
    };

    const onSortEnd = ({
      oldIndex,
      newIndex,
    }: {
      oldIndex: number;
      newIndex: number;
    }): void => {
      setDragItemIndex(null);
      move(oldIndex, newIndex);
    };

    const sortableItemElement = useMemo(() => {
      return cultureStats
        .filter((item) => !item._destroy)
        .map((item, index) => {
          const showControls =
            dragItemIndex === null
              ? hoverItemIndex === index
              : dragItemIndex === index;
          return (
            <SortableItem key={item.id || uuidv4()} index={index}>
              <EditableCard
                data={{ ...item }}
                isUrl={false}
                title={'Edit culture & values'}
                index={index}
                onSave={update}
                onRemove={remove}
                onMouseEnter={() => setHoverItemIndex(index)}
                onMouseLeave={() => setHoverItemIndex(null)}
                key={index}
                trigger={
                  <Trigger className={styles.Trigger}>
                    <Icon iconName="drag-and-drop" size="xsmallplus" />
                  </Trigger>
                }
                isGrabbing={dragItemIndex === index}
                showControls={showControls}
              />
            </SortableItem>
          );
        });
    }, [cultureStats, dragItemIndex]);

    return (
      <div id={`section_${progressDetails?.id}`} className={sectionClass}>
        {showProgressBannerState && (
          <Gamification
            profileCompletePercent={profileCompletePercent}
            nonCompletedSection={nonCompletedSection}
            progressDetails={progressDetails}
            handleProfileCompletionButton={() => {
              setShowProgressBannerState(false);
              handleProfileCompletionButton();
            }}
            handleNextButton={() => {
              setShowProgressBannerState(false);
              handleNextButton && handleNextButton();
            }}
            showNextButton={showNextButton}
          />
        )}
        <SectionWrapper
          title="Company identity"
          description="Attract candidates who share your values and are excited about your company's mission, purpose & culture. This is what sets you apart from your competitors."
          className={cn({ [styles.noBorderRadius]: showProgressBannerState })}
        >
          <Row>
            <Col span={24} className={styles.textAreaSpacing}>
              <TextArea
                rows={4}
                label="Company mission & purpose"
                {...register('description', {
                  required: 'Company mission & purpose is required',
                })}
                isValid={!errors.description}
                helperText={errors.description?.message}
                labelClassName={styles.labelField}
                textAreaClassName={styles.textArea}
                placeholder="Add your company's mission & purpose"
                infoToolTip={
                  <Tooltip
                    content="Explain clearly in one paragraph: What does your company do and what is your ultimate mission & purpose?"
                    position="top"
                    xAlign="center"
                    color="dark"
                    className={styles.tooltipContainer}
                    hasWhitespaceNoWrap={false}
                  >
                    <Icon
                      iconName="icon_info-outline"
                      className={styles.tooltipContainerIcon}
                      size="small"
                    />
                  </Tooltip>
                }
                maxLength={600}
              />
            </Col>

            <Col span={24}>
              <div className={styles.labelField}>
                Culture & values{' '}
                <span className={styles.optional}>(Optional)</span>
              </div>

              <SortableList
                lockAxis="y"
                lockToContainerEdges={true}
                useDragHandle={true}
                onSortStart={onSortStart}
                onSortEnd={onSortEnd}
              >
                {sortableItemElement}
              </SortableList>

              <AddNewCard
                isUrl={false}
                title={'Add culture & values'}
                onSave={append}
                placeHolder="People First"
                modalPlaceholders={{
                  title: 'People First',
                  subTitle:
                    'We prioritise our people, valuing their diverse perspectives and creating a supportive environment that encourages growth, collaboration, and work-life balance',
                }}
                itemCount={sortableItemElement?.length}
              />
            </Col>
          </Row>
        </SectionWrapper>
      </div>
    );
  }
);

IdentityDetails.displayName = 'IdentityDetails';
